import { AddressModel, AddressClass } from "./address";
import { PropertyTypeId } from "./propertyType";
import { FloorId } from "./floor";
import { OwnershipTypeId } from "./ownership";
import { observable, action, makeObservable } from "mobx";
import Validator, { ValidatorModel, ValidatedValue } from "./validator";
import { ErrorMessages } from "./errorMessage";


export interface EstateModel {
    address: AddressModel;
    area: ValidatedValue<number>;
    constructionYear: ValidatedValue<number | null>;
    flammableBuildingStructure: boolean;
    floor: FloorId | null;
    overhaulYear: ValidatedValue<number | null>;
    ownershipType: ValidatedValue<OwnershipTypeId>;
    propertyType: PropertyTypeId | null;

    setPropertyType: (propertyType: PropertyTypeId) => void;
    setFloor: (floor: FloorId) => void;
    setArea: (area: number) => void;
    setConstructionYear: (constructionYear: number) => void;
    setOverhaulYear: (overhaulYear: number) => void;
    setFlammableBuildingStructure: (value: boolean) => void;
    setOwnershipType: (value: OwnershipTypeId) => void;
}

interface EstateArguments {
    address?: AddressModel;
    area?: number;
    constructionYear?: number | null;
    flammableBuildingStructure?: boolean;
    floor?: FloorId | null;
    overhaulYear?: number | null;
    ownershipType?: OwnershipTypeId;
    propertyType?: PropertyTypeId | null;
}



export class DefaultEstate implements EstateModel {
    estateValidators: ValidatorModel = {
        area: [{ name: "areaIsNotEmpty", fn: Validator.isNotEmpty, errorMessage: ErrorMessages.required }],
        constructionYear: [
            { name: "constructionYearIsNotEmpty", fn: Validator.isNotEmpty, errorMessage: ErrorMessages.required },
            {
                name: "constructionYearEarlierThanCurrent",
                fn: Validator.yearEarlierThanCurrent,
                errorMessage: ErrorMessages.year.earlierThanCurrent
            },
            {
                name: "constructionYearMin",
                fn: Validator.min(1900),
                errorMessage: "Podany rok nie może być wcześniejszy niż 1900"
            }
        ],
        overhaulYear: [
            { name: "overhaulYearIsNotEmpty", fn: Validator.isNotEmpty, errorMessage: ErrorMessages.required },
            {
                name: "overhaulYearEarlierThanCurrent",
                fn: Validator.yearEarlierThanCurrent,
                errorMessage: ErrorMessages.year.earlierThanCurrent
            },
            {
                name: "overhaulYearMin",
                fn: (value) => { return Number(value) >= Number(this.constructionYear.value); },
                errorMessage: "Podany rok nie może być wcześniejszy niż rok budowy"
            }
        ],
        ownershipType: [
            { name: "ownershipTypeIsNotEmpty", fn: Validator.isNotEmpty, errorMessage: ErrorMessages.required }
        ]
    };


    @observable address: AddressModel = AddressClass.default();
    @observable area: ValidatedValue<number> = new ValidatedValue(0, this.estateValidators.area);
    @observable constructionYear: ValidatedValue<number | null> = new ValidatedValue(null, this.estateValidators.constructionYear);
    @observable flammableBuildingStructure = false;
    @observable floor: FloorId | null;
    @observable overhaulYear: ValidatedValue<number | null> = new ValidatedValue(null, this.estateValidators.overhaulYear);
    @observable ownershipType: ValidatedValue<OwnershipTypeId> = new ValidatedValue(OwnershipTypeId.none, this.estateValidators.ownershipType);
    @observable propertyType: PropertyTypeId | null;

    constructor(args?: EstateArguments) {
        makeObservable(this);

        this.address = args?.address ?? AddressClass.default();
        this.area.value = args?.area ?? 0;
        this.constructionYear.value = args?.constructionYear ?? null;
        this.flammableBuildingStructure = args?.flammableBuildingStructure ?? false;
        this.floor = args?.floor ?? null;
        this.overhaulYear.value = args?.overhaulYear ?? null;
        this.ownershipType.value = args?.ownershipType ?? OwnershipTypeId.none;
        this.propertyType = args?.propertyType ?? null;
    }

    @action
    public setPropertyType(propertyType: PropertyTypeId) {
        this.propertyType = propertyType;
    }

    @action
    public setFloor(floor: FloorId) {
        this.floor = floor;
    }

    @action
    public setArea(area: number) {
        this.area.setValue(area);
    }

    @action
    public setConstructionYear(constructionYear: number) {
        this.constructionYear.value = constructionYear;
    }

    @action
    public setOverhaulYear(overhaulYear: number) {
        this.overhaulYear.value = overhaulYear;
    }

    @action
    public setFlammableBuildingStructure(value: boolean) {
        this.flammableBuildingStructure = value;
    }

    @action
    public setOwnershipType(value: OwnershipTypeId) {
        this.ownershipType.setValue(value);
    }

    static example() {
        return new DefaultEstate({
            address: AddressClass.example(),
            area: 123,
            constructionYear: 2000,
            flammableBuildingStructure: false,
            floor: null,
            overhaulYear: null,
            ownershipType: OwnershipTypeId.ownership,
            propertyType: PropertyTypeId.detachedHouse
        });
    }
}
